// Conexión de Mercado Libre, por marca. Los tokens nunca pasan por acá: los guarda
// y los usa atlas-backend (ADR-2026-09-28-mercadolibre-vive-en-el-backend).
//
// Flujo:
//   1. click → `abrirVentanaDeMercadoLibre()` (SINCRÓNICO, antes de cualquier await)
//   2. `conectarMercadoLibreEnVentana(brandId, popup)`:
//      POST /v1/mercadolibre/conectar → URL de Mercado Libre → la persona acepta →
//      el backend deja la conexión pendiente → ml-conectado.html avisa el reclamo →
//      POST /v1/mercadolibre/reclamar la activa con la sesión de la MISMA persona.

import { atlasBackendFetch, atlasBackendGet } from "./atlasBackend";
import {
  CLAVE_AVISO_ML,
  codigoDeError,
  ErrorDeMercadoLibre,
  ESPERA_MAXIMA_ML_MS,
  estadoDesdeRespuesta,
  EVENTO_CAMBIO_DE_MERCADOLIBRE,
  leerAvisoDeMercadoLibre,
  type AvisoDeMercadoLibre,
  type EstadoDeMercadoLibre,
} from "./mercadoLibreConexionLogica";

export {
  ErrorDeMercadoLibre,
  EVENTO_CAMBIO_DE_MERCADOLIBRE,
  mensajeDeErrorDeMercadoLibre,
  type EstadoDeMercadoLibre,
} from "./mercadoLibreConexionLogica";

function avisarCambio() {
  try {
    window.dispatchEvent(new Event(EVENTO_CAMBIO_DE_MERCADOLIBRE));
  } catch {
    /* noop */
  }
}

/**
 * Abre la ventana del login. **Llamar SIEMPRE de forma sincrónica dentro del
 * handler del click**, nunca después de un `await`: Safari y todos los
 * navegadores de iPhone la bloquean (ADR-2026-08-07-tiendanube-popup-safari).
 */
export function abrirVentanaDeMercadoLibre(): Window | null {
  return window.open("/ml-conectando.html", "ml_connect", "width=620,height=760");
}

async function leerJson(r: Response): Promise<unknown> {
  try {
    return await r.json();
  } catch {
    return null;
  }
}

/** POST al backend; convierte cualquier falla en `ErrorDeMercadoLibre`. */
async function post<T>(ruta: string, body: Record<string, unknown>): Promise<T> {
  let r: Response;
  try {
    r = await atlasBackendFetch(ruta, body);
  } catch (e) {
    const sinSesion = e instanceof Error && /sesión/i.test(e.message);
    throw new ErrorDeMercadoLibre(sinSesion ? "sin_sesion" : "red");
  }
  const cuerpo = await leerJson(r);
  if (!r.ok) throw new ErrorDeMercadoLibre(codigoDeError(r.status, cuerpo));
  return cuerpo as T;
}

function cerrar(popup: Window | null | undefined) {
  try {
    popup?.close();
  } catch {
    /* noop */
  }
}

function olvidarAviso() {
  try {
    localStorage.removeItem(CLAVE_AVISO_ML);
  } catch {
    /* noop */
  }
}

/**
 * Espera el aviso de `ml-conectado.html`: por postMessage (lo normal) o por
 * localStorage (si el navegador perdió el `opener`). Rechaza si cierran la
 * ventana antes o si se agota el tiempo.
 */
function esperarAviso(popup: Window): Promise<AvisoDeMercadoLibre> {
  return new Promise((resolve, reject) => {
    let listo = false;

    const terminar = (aviso: AvisoDeMercadoLibre | null, err?: ErrorDeMercadoLibre) => {
      if (listo) return;
      listo = true;
      window.removeEventListener("message", alMensaje);
      window.removeEventListener("storage", alStorage);
      clearInterval(vigia);
      clearTimeout(limite);
      olvidarAviso();
      if (aviso) resolve(aviso);
      else reject(err);
    };

    const delStorage = () => {
      try {
        return leerAvisoDeMercadoLibre(localStorage.getItem(CLAVE_AVISO_ML));
      } catch {
        return null;
      }
    };

    const alMensaje = (e: MessageEvent) => {
      // Sólo la página de nuestro propio origen: el reclamo no lo manda nadie más.
      if (e.origin !== window.location.origin) return;
      const aviso = leerAvisoDeMercadoLibre(e.data);
      if (aviso) terminar(aviso);
    };
    const alStorage = (e: StorageEvent) => {
      if (e.key !== CLAVE_AVISO_ML) return;
      const aviso = leerAvisoDeMercadoLibre(e.newValue);
      if (aviso) terminar(aviso);
    };

    window.addEventListener("message", alMensaje);
    window.addEventListener("storage", alStorage);

    // Si cierran la ventana, puede que el aviso ya esté en localStorage
    // (el evento `storage` no siempre llega antes que el cierre).
    const vigia = setInterval(() => {
      if (!popup.closed) return;
      const aviso = delStorage();
      terminar(aviso, aviso ? undefined : new ErrorDeMercadoLibre("ventana_cerrada"));
    }, 700);

    const limite = setTimeout(() => {
      cerrar(popup);
      terminar(null, new ErrorDeMercadoLibre("tiempo"));
    }, ESPERA_MAXIMA_ML_MS);
  });
}

export interface MercadoLibreConectado {
  nickname: string | null;
}

/**
 * Conecta el Mercado Libre de una marca sobre una ventana **ya abierta por el
 * click** (`abrirVentanaDeMercadoLibre`). Tira `ErrorDeMercadoLibre` con un
 * mensaje listo para mostrar.
 */
export async function conectarMercadoLibreEnVentana(
  brandId: string | null,
  popup: Window | null | undefined,
): Promise<MercadoLibreConectado> {
  if (!popup) throw new ErrorDeMercadoLibre("popup_bloqueado");
  olvidarAviso(); // que no se cuele el aviso de un intento anterior

  let url: string;
  try {
    ({ url } = await post<{ url: string }>("/v1/mercadolibre/conectar", { brand_id: brandId }));
  } catch (e) {
    cerrar(popup);
    throw e;
  }
  // La URL la arma el backend, pero igual: sólo se navega a la autorización de Mercado Libre.
  if (!/^https:\/\/auth\.mercadoli(b|v)re\.com(\.[a-z]{2})?\//.test(url)) {
    cerrar(popup);
    throw new ErrorDeMercadoLibre("error");
  }
  if (popup.closed) throw new ErrorDeMercadoLibre("ventana_cerrada");
  popup.location.href = url;

  const aviso = await esperarAviso(popup);
  cerrar(popup);
  if ("error" in aviso) throw new ErrorDeMercadoLibre(aviso.error);

  const r = await post<{ nickname?: string | null }>("/v1/mercadolibre/reclamar", { reclamo: aviso.reclamo });
  avisarCambio();
  return { nickname: r?.nickname ?? null };
}

/** Estado de la conexión de una marca. Liviano: el backend sólo lee su base. */
export async function estadoDeMercadoLibre(brandId: string | null): Promise<EstadoDeMercadoLibre> {
  try {
    const q = brandId ? `?brand_id=${encodeURIComponent(brandId)}` : "";
    const r = await atlasBackendGet(`/v1/mercadolibre/estado${q}`);
    return estadoDesdeRespuesta(r.status, await leerJson(r));
  } catch {
    return estadoDesdeRespuesta(0, null);
  }
}

/** Desconecta el Mercado Libre de una marca (revoca el permiso y borra la conexión). */
export async function desconectarMercadoLibre(brandId: string | null): Promise<void> {
  await post("/v1/mercadolibre/desconectar", { brand_id: brandId });
  avisarCambio();
}
